import React, {useState, useMemo} from "react";
import {Document, Page, pdfjs} from "react-pdf";
import type {PDFDocumentProxy} from "pdfjs-dist";
import {FlexRow} from "@reside/ui";

import {PDFContainer, PDFDocument, PDFViewerContainer} from "./styles";
import {DocumentPreviewBar} from "./atoms/DocumentPreviewBar";
import {Spinner} from "../spinner";
import {PDFProps} from "./types";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export const PDFViewer = ({
  pdfDocumentName,
  customOnLoadSuccess,
  file,
  isGenerating,
}: PDFProps) => {
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [numPages, setNumPages] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);

  const pages = useMemo(() => {
    if (!numPages) return [];

    return Array.from(new Array(numPages), (_el, index) => (
      <Page
        key={`page_${index + 1}`}
        pageNumber={index + 1}
        width={containerRef?.clientWidth}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    ));
  }, [numPages, containerRef?.clientWidth]);

  const onDocumentLoadSuccess = ({
    numPages: nextNumPages,
  }: PDFDocumentProxy) => {
    setNumPages(nextNumPages);
    setIsLoading(false);
    customOnLoadSuccess?.();
  };

  return (
    <PDFViewerContainer>
      <DocumentPreviewBar
        pdfDocumentName={pdfDocumentName}
        isLoadingPdf={isLoading}
        isGenerating={isGenerating}
        file={file}
      />

      <PDFContainer className="pdf-container">
        <PDFDocument className="pdf-document" ref={setContainerRef}>
          <Document
            file={file}
            noData={
              // If generation is done but data is not loaded, inform user that something went wrong.
              isGenerating
                ? "Generating your PDF..." // This is just backup. It should not be shown because of loading state in the SignOverlay component.
                : "Something went wrong when generating the PDF. Please try again by refreshing the page."
            }
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <FlexRow style={{height: "fit-content", width: "100%"}}>
                <Spinner />
              </FlexRow>
            }
          >
            {pages}
          </Document>
        </PDFDocument>
      </PDFContainer>
    </PDFViewerContainer>
  );
};
